import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { CtaLink, LibraryPageContent } from "../content/types";

function Cta({ cta, variant }: { cta: CtaLink; variant: "primary" | "quiet" }) {
  const className = variant === "primary" ? "btn btn--primary" : "btn btn--quiet";
  if (cta.external || cta.href.startsWith("#")) {
    return (
      <a className={className} href={cta.href}>
        {cta.label}
      </a>
    );
  }
  return (
    <Link className={className} to={cta.href}>
      {cta.label}
    </Link>
  );
}

/**
 * Index of published material — the library (/resources) and the documentation
 * signpost (/docs) share this template.
 *
 * The active filter lives in the query string rather than in component state so
 * a filtered view is a linkable thing: the Resources menu can point straight at
 * `?type=code`, and the browser back button behaves. An unrecognized `type`
 * falls back to showing everything, because a URL someone typed or a link that
 * outlived a renamed filter should not produce an empty page.
 */
export function LibraryPage({ content }: { content: LibraryPageContent }) {
  const [params, setParams] = useSearchParams();
  const requested = params.get("type");
  const active =
    requested && content.filters.some((f) => f.id === requested)
      ? requested
      : undefined;

  useEffect(() => {
    document.title = `${content.headline} — Odingard`;
  }, [content.headline]);

  const entries = active
    ? content.entries.filter((entry) => entry.type === active)
    : content.entries;

  const select = (id?: string) => {
    if (id) setParams({ type: id });
    else setParams({});
  };

  return (
    <main className="page pdp library" id="main">
      <section className="pdp__hero">
        <p className="page__eyebrow">{content.eyebrow}</p>
        <h1>{content.headline}</h1>
        <p className="page__lede">{content.lede}</p>
      </section>

      <section className="pdp__section" id="library">
        <div className="library__filters" role="group" aria-label="Filter by type">
          <button
            type="button"
            className="library__filter"
            aria-pressed={active === undefined}
            onClick={() => select(undefined)}
          >
            All ({content.entries.length})
          </button>
          {content.filters.map((filter) => {
            const count = content.entries.filter(
              (entry) => entry.type === filter.id,
            ).length;
            return (
              <button
                key={filter.id}
                type="button"
                className="library__filter"
                aria-pressed={active === filter.id}
                onClick={() => select(filter.id)}
              >
                {filter.label} ({count})
              </button>
            );
          })}
        </div>

        <ul className="library__list">
          {entries.map((entry) => (
            <li key={entry.href} className="library__item">
              {entry.external ? (
                <a
                  className="library__title"
                  href={entry.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {entry.title} ↗
                </a>
              ) : (
                <Link className="library__title" to={entry.href}>
                  {entry.title}
                </Link>
              )}
              <p className="library__blurb">{entry.blurb}</p>
              <p className="library__meta">{entry.meta}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="pdp__close" id="close">
        <h2>{content.close.heading}</h2>
        <p className="pdp__body">{content.close.body}</p>
        <div className="pdp__ctas">
          {content.close.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>
    </main>
  );
}
